import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Parameter definitions
const parameterDefinitions = [
  // { key: "3A", label: "3A" },
  // { key: "3B", label: "3B" },
  // { key: "3C", label: "3C" },
  { key: "zip_url", label: "Zip URL" },
];

// Default data object
const defaultData = {
  files: {
    "3A": "N/A",
    "3B": "N/A",
    "3C": "N/A",
  },
  zip_url: "N/A",
};

const CellToRibbonPeelStrengthTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Filter valid data items
  const validData = useMemo(() => {
    return (data || []).filter((item) => {
      return item && item.inspectionDate && item.inspectionShift && item.form_data;
    });
  }, [data]);

  // Get all dates in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data shifts
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(validData.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["1"];
  }, [shifts, validData]);

  // Build key-value map for quick lookup: `${date}_${shift}`
  const dataMap = useMemo(() => {
    const map = {};
    validData.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      const key = `${date}_${shift}`;
      map[key] = {
        ...defaultData,
        files: {
          ...defaultData.files,
          ...(item.form_data?.files || {}),
        },
        zip_url:
          item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url)
            ? item.zip_url.trim()
            : "N/A",
      };
    });
    return map;
  }, [validData]);

  // Column coordinate list: [date, shift]
  const columnTree = useMemo(() => {
    const cols = [];
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        cols.push({
          date,
          shift,
          id: `${date}_${shift}`,
        });
      });
    });
    return cols;
  }, [dates, effectiveShifts]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: 180,
      value: 120,
    };

    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.max(widths.parameter, estimateTextWidth(param.label));
    });

    return widths;
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    return parameterDefinitions.map((param) => {
      const row = {
        parameter: param.label,
        paramKey: param.key,
        data: {},
      };

      columnTree.forEach(({ id }) => {
        const entry = dataMap[id] || defaultData;
        row.data[id] =
          param.key === "zip_url"
            ? entry.zip_url
            : entry.files[param.key] || "N/A";
      });

      return row;
    });
  }, [dataMap, columnTree]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });

    let colIdx = 1;
    const shiftSpan = effectiveShifts.length;

    dates.forEach((date) => {
      row1.push(date);
      for (let i = 1; i < shiftSpan; i++) row1.push("");

      if (shiftSpan > 1) {
        merges.push({
          s: { r: 0, c: colIdx },
          e: { r: 0, c: colIdx + shiftSpan - 1 },
        });
      }

      effectiveShifts.forEach((shift) => {
        row2.push(`Shift ${shift}`);
        colIdx += 1;
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      columnTree.forEach((col) => {
        const val = row.data[col.id] || "N/A";
        if (row.paramKey === "zip_url" && val !== "N/A" && val.startsWith("http")) {
          r.push("LINK");
        } else {
          r.push(val);
        }
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    tableRows.forEach((row, rowIndex) => {
      if (row.paramKey === "zip_url") {
        columnTree.forEach((col, colIndex) => {
          const rawUrl = row.data[col.id];
          if (
            rawUrl &&
            rawUrl !== "N/A" &&
            typeof rawUrl === "string" &&
            rawUrl.trim().toLowerCase().startsWith("http")
          ) {
            const cleanUrl = rawUrl.trim();
            const cellAddress = XLSX.utils.encode_cell({
              r: rowIndex + 2,
              c: colIndex + 1,
            });

            ws[cellAddress] = {
              t: "s",
              v: "LINK",
              l: { Target: cleanUrl, Tooltip: cleanUrl },
            };
          }
        });
      }
    });

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 22 },
      ...Array(columnTree.length).fill({ wch: 16 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Peel Strength");

    const fileName = generateExcelFileName("Cell_to_Ribbon_Peel_Strength", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", bookSST: false });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  const shiftsPerDate = effectiveShifts.length;

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Cell to Ribbon Peel Strength
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || columnTree.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Parameter + Dates (merged across shifts) */}
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={shiftsPerDate}
                  columnWidth={columnWidths.value * shiftsPerDate}
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Sub-headers for Shifts */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.map((shift) => (
                  <CustomTableCell
                    key={`${date}_${shift}`}
                    columnWidth={columnWidths.value}
                  >
                    {shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {columnTree.map((col) => {
                    const value = row.data[col.id] || "N/A";
                    return (
                      <BodyTableCell key={col.id} columnWidth={columnWidths.value}>
                        {row.paramKey === "zip_url" && value !== "N/A" ? (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "underline" }}
                          >
                            LINK
                          </a>
                        ) : (
                          value
                        )}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default CellToRibbonPeelStrengthTable;